import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Tabs, Collapse, Card, Button, Typography, Tag, Spin, Avatar, message } from "antd";
import { FileTextOutlined, LockOutlined, UserOutlined, VideoCameraOutlined, PlayCircleOutlined, CheckCircleOutlined, ShoppingCartOutlined, ThunderboltOutlined } from "@ant-design/icons";
import LearnerCourseService from "../services/LearnerCourseService";
import CartApiService from '../services/CartApiService';
import { useAuth } from "../contexts/AuthContext";
import { Role } from "../constants/Role";
import KeycloakService from '../services/KeycloakService';

import LearnerApiService from '../services/LearnerApiService';
import "../styles/CourseDetail.css";

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const CourseDetail = () => {
  const { courseSlug } = useParams();
  const { authenticated, hasAnyRole } = useAuth();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeKeys, setActiveKeys] = useState([]);
  const [inCart, setInCart] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await LearnerCourseService.getLearnerCourseBySlug(courseSlug);
        setCourse(res.data || null);
      } catch (err) {
        setError("Unable to load course information");
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [courseSlug]);

  useEffect(() => {
    // Reset activeKeys khi course thay đổi
    setActiveKeys([]);
    // Kiểm tra xem khóa học đã có trong cart chưa
    const checkInCart = async () => {
      if (!course?._id || !authenticated) return;
      // Chỉ kiểm tra cart cho role LEARNER
      if (!hasAnyRole([Role.LEARNER])) {
        setInCart(false);
        return;
      }
      try {
        const cartRes = await CartApiService.getCart();
        const found = cartRes.data?.courses?.some(c => c._id === course._id);
        setInCart(!!found);
      } catch (e) {
        // ignore
      }
    };
    checkInCart();
  }, [course, authenticated, hasAnyRole]);


  useEffect(() => {
    // check da mua chua 
    const checkPurchased = async () => {
      if (!course?._id || !authenticated) return;
      try {
        const res = await LearnerApiService.getMyCourses({ page: 1, limit: 100 });
        const found = res.courses?.some(c => c._id === course._id);
        setIsPurchased(!!found);
      } catch (e) {
        // ignore
      }
    };
    checkPurchased();
  }, [course, authenticated]);

  const handleAddToCart = async () => {
    // Kiểm tra xem người dùng đã đăng nhập chưa
    if (!authenticated) {
      message.warning('Please login to add courses to cart!');
      // Redirect to Keycloak authentication with current URL as callback
      KeycloakService.login(window.location.pathname);
      return;
    }

    // Chỉ cho phép role LEARNER thêm vào cart
    if (!hasAnyRole([Role.LEARNER])) {
      message.warning('Only learners can add courses to cart!');
      return;
    }

    try {
      const res = await CartApiService.addToCart(course._id);
      if (res.success) {
        message.success('Course added to cart successfully!');
        setInCart(true);
        // Cập nhật cart dropdown và cart count
        window.dispatchEvent(new Event('cart-changed'));
      } else {
        message.error('Add to cart failed!');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        message.error('Your session has expired. Please log in again!');
        KeycloakService.login(window.location.pathname);
      } else {
        message.error('Add to cart failed!');
      }
    }
  };

  if (loading) return <Spin style={{ margin: 40 }} size="large" />;
  if (error) return <div style={{ textAlign: 'center', padding: 40 }}>{error}</div>;
  if (!course) return <div style={{ textAlign: 'center', padding: 40 }}>Không tìm thấy khoá học</div>;

  const getSectionStats = (section) => {
    let totalLessons = section.lessons?.length || 0;
    // let totalMins = section.lessons?.reduce((sum, l) => sum + (parseInt((l.duration||'0').split(":")[0]) || 0), 0);
    // return `${totalLessons} Lessons   ${totalMins} Mins`;
    return `${totalLessons} Lessons `;

  };

  const getLessonIcon = (lesson) => {
    if (lesson.type === "Video") {
      return <VideoCameraOutlined className="lesson-icon" />;
    }
    return <FileTextOutlined className="lesson-icon" />;
  };

  return (
    <div className="course-detail-page">
      <Row gutter={32}>
        {/* Main Content */}
        <Col xs={24} md={16}>
          <Card>
            <Title level={2}>{course.name}</Title>
            <div style={{ margin: '16px 0' }}>
                <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
                <Text>Created by <Text strong>{course.mentorName || course.author || 'Anonymous'}</Text></Text>
                {course.category?.name ? <Tag color="blue" style={{ marginLeft: 16 }}>{course.category.name}</Tag> : null}
            </div>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>{course.description}</Paragraph>
            
            <Tabs defaultActiveKey="1">
              <TabPane tab={<span style={{ fontWeight: 600 }}>Curriculum</span>} key="1">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <span style={{ color: '#888', fontSize: 15 }}>
                    {course.sections?.length || 0} Sections
                  </span>
                  {course.sections && course.sections.length > 0 && (
                    activeKeys.length === course.sections.length ? (
                      <Button
                        size="middle"
                        style={{
                          background: '#fff',
                          color: '#283593',
                          borderRadius: 20,
                          fontWeight: 500,
                          padding: '0 18px',
                          border: '1.5px solid #283593',
                          transition: 'all 0.2s',
                        }}
                        className="expand-collapse-btn"
                        onClick={() => setActiveKeys([])}
                      >
                        Collapse All
                      </Button>
                    ) : (
                      <Button
                        size="middle"
                        style={{
                          background: '#fff',
                          color: '#283593',
                          borderRadius: 20,
                          fontWeight: 500,
                          padding: '0 18px',
                          border: '1.5px solid #283593',
                          transition: 'all 0.2s',
                        }}
                        className="expand-collapse-btn"
                        onClick={() => setActiveKeys(course.sections.map(s => s._id))}
                      >
                        Expand All
                      </Button>
                    )
                  )}
                </div>
                <Collapse
                  bordered={false}
                  className="course-curriculum"
                  activeKey={activeKeys}
                  onChange={setActiveKeys}
                >
                  {course.sections?.map((section) => (
                    <Panel
                      header={
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{section.name}</span>
                          <Text type="secondary" style={{ fontSize: 13 }}>{getSectionStats(section)}</Text>
                        </div>
                      }
                      key={section._id}
                    >
                      {section.lessons?.map((lesson) => (
                        <div className="lesson-item" key={lesson._id}>
                          {getLessonIcon(lesson)}
                          <span style={{ color: lesson.isPreview ? '#1890ff' : '#222', cursor: lesson.isPreview ? 'pointer' : 'default' }}>
                            {lesson.name}
                          </span>
                          <span style={{ marginLeft: 'auto', color: '#888', fontSize: 13 }}>{lesson.duration || ''}</span>
                          {!lesson.isPreview && <LockOutlined className="lesson-lock-icon" />}
                        </div>
                      ))}
                    </Panel>
                  ))}
                </Collapse>
              </TabPane>
              <TabPane tab={<span style={{ fontWeight: 600 }}>Mentor</span>} key="2">
                <div className="mentor-tab" style={{ padding: '16px 0', display: 'flex', alignItems: 'center' }}>
                  <Avatar size={64} icon={<UserOutlined />} />
                  <div>
                    <Title level={5}>{course.mentorName || course.author || 'Mentor Name'}</Title>
                    <Paragraph type="secondary">{course.mentorBio || 'Mentor bio or introduction...'}</Paragraph>
                  </div>
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </Col>
        {/* Sidebar */}
        <Col xs={24} md={8}>
          <div className="sticky-sidebar">
            <Card
              cover={course.imageUrl && <img src={course.imageUrl} alt={course.name} className="course-sidebar-image" />}
            >
              <Title level={4}>
                {course.price === 0 || course.price === 'Free' ? 'Free' : `$${course.price}`}
                {course.priceBeforeDiscount && (
                  <Text delete style={{ color: '#888', marginLeft: 12, fontSize: 16 }}>${course.priceBeforeDiscount}</Text>
                )}
              </Title>
              {/* Button group logic */}
              {isPurchased ? (
                <Button
                  type="primary"
                  block
                  size="large"
                  style={{ background: '#16a34a', border: 'none', color: '#fff', fontWeight: 600, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  icon={<PlayCircleOutlined style={{ fontSize: 20, marginRight: 8 }} />}
                  onClick={() => navigate(`/learner/my-courses/${course.slug}`)}
                >
                  Go to Learning Zone
                </Button>
              ) : inCart ? (
                <Button
                  type="default"
                  block
                  size="large"
                  style={{ background: '#e6f0ff', border: '1.5px solid #91caff', color: '#1677ff', fontWeight: 600, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  icon={<CheckCircleOutlined style={{ fontSize: 18, marginRight: 5 }} />}
                  disabled
                >
                  In Cart
                </Button>
              ) : (
                <div style={{ display: 'flex', gap: 12 }}>
                    <Button
                    type="primary"
                    size="large"
                    style={{ flex: 1, background: '#f59e42', border: 'none', color: '#fff', fontWeight: 600, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    icon={<ThunderboltOutlined style={{ fontSize: 18, marginRight: 5 }} />}
                    onClick={async () => {
                      if (!authenticated) {
                        KeycloakService.login(window.location.pathname);
                        return;
                      }
                      
                      // Chỉ cho phép role LEARNER mua khóa học
                      if (!hasAnyRole([Role.LEARNER])) {
                        message.warning('Only learners can purchase courses!');
                        return;
                      }
                      
                      if (!inCart) {
                        const res = await CartApiService.addToCart(course._id);
                        if (res.success) {
                          setInCart(true);
                          window.dispatchEvent(new Event('cart-changed'));
                          message.success('Order created successfully!');
                        } else {
                          message.error('Failed to create order!');
                          return;
                        }
                      }
                      localStorage.setItem('selectedCourseIds', JSON.stringify([course._id]));
                      navigate('/cart/checkout');
                    }}
                    className="buy-now-btn"
                  >
                    Buy Now
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    style={{ flex: 1, fontWeight: 600, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    icon={<ShoppingCartOutlined style={{ fontSize: 18, marginRight: 5 }} />}
                    onClick={handleAddToCart}
                    className="add-to-cart"
                  >
                    Add To Cart
                  </Button>
                
                </div>
              )}
              <div style={{ marginTop: 16 }}>
                <Title level={5}>This course includes:</Title>
                <Paragraph>
                  <ul>
                    <li>Content for {course.sections?.length || 0} sections</li>
                    <li>Full lifetime access</li>
                    <li>Access on mobile and TV</li>
                  </ul>
                </Paragraph>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CourseDetail;

